<template>
    <div class="transparency-page">
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && transparency.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="transparency"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                            @on-name-change="onNameChange"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :key="informationKey"
                            :campuses="transparency.campuses"
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="transparency"
                            :slug="transparency.slug"
                            :options="{ slugBasicUrl: 'transparency' }"
                            :slug-error="slugError"
                            @on-slug-change="onSlugChange"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-campuses-change="onCampusesChange"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required } from 'vuelidate/lib/validators';
import AddEditForm from '@/components/transparency/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';
import types from '@/../../common/transparency-type';

export default {
    page: {
        title: 'Manage Transparency'
    },

    validations: {
        transparency: {
            name: {
                required
            },
            content: {
                atLeastOneItem: val => val && val.length > 0
            },
            meetingDate: {
                required
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit transparency' : 'Add transparency',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Transparency',
                    to: '/transparency'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            transparency: {
                content: [],
                type: types[0],
                campuses: []
            },
            informationKey: 0,
            serverErrors: [],
            transparencyValueStringify: ''
        };
    },

    computed: {
        sections() {
            const sections = {
                permalink: true,
                campuses: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        slugError() {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.transparency,
                this.serverErrors,
                'slug'
            );
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.transparency = await this.getTransparency(this.id);
                this.transparencyValueStringify = JSON.stringify(
                    this.transparency
                );
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }

            return;
        }

        try {
            const { rows } = await this.getCampuses({ perPage: 5 });

            this.transparency.campuses = rows;
        } catch (error) {
            console.error(error);

            this.$toasterError();
        }
    },

    methods: {
        ...mapActions({
            addTransparency: 'transparency/store',
            deleteTransparency: 'transparency/destroy',
            getTransparency: 'transparency/show',
            updateTransparency: 'transparency/update',
            getCampuses: 'campuses/index'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const campuses = this.transparency.campuses.map(
                campus => campus.id
            );

            const data = {
                ...this.transparency,
                campuses
            };

            try {
                if (!this.editMode) {
                    const added = await this.addTransparency(data);

                    this.$router.push(`/transparency/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateTransparency({
                        id: this.id,
                        data
                    });

                    this.transparency.updatedAt = updatedAt;

                    this.$toaster('Transparency updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteTransparency(this.id);

                this.$router.push('/transparency');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onNameChange() {
            if (this.editMode) {
                return;
            }

            this.onSlugChange(this.transparency.name);
            this.informationKey++;
        },

        onSlugChange(slug) {
            this.transparency.slug = slug;
        },

        onCampusesChange(campuses) {
            this.transparency.campuses = campuses;
            this.informationKey++;
        }
    }
};
</script>
